<template>
  <div class="page-container">
    <!-- 头部 -->
    <div class="page-header">
      <div>
        <h2 class="page-title">核销记录</h2>
      </div>
      <div class="page-header-extra">
        <span>共 <b>{{ total }}</b> 次</span>
        <span class="divider">|</span>
        <span>成功 <b>{{ resultCount(1) }}</b></span>
        <span class="divider">|</span>
        <span class="text-danger">失败 <b>{{ resultCount(0) }}</b></span>
      </div>
    </div>

    <!-- 搜索 + 操作 -->
    <el-card class="search-card" shadow="never">
      <div class="search-bar">
        <el-form :inline="true" :model="query" size="small" class="search-form">
          <el-form-item label="关键字">
            <el-input
              v-model="query.keyword"
              placeholder="核销码 / 昵称 / 券名称"
              clearable
              style="width: 220px"
              @keyup.enter.native="handleSearch"
            />
          </el-form-item>
          <el-form-item label="核销结果">
            <el-select v-model="query.result" placeholder="全部" clearable style="width: 130px">
              <el-option v-for="r in VERIFY_RESULT" :key="r.value" :label="r.label" :value="r.value" />
            </el-select>
          </el-form-item>
          <el-form-item label="核销时间">
            <el-date-picker
              v-model="query.dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd"
              style="width: 260px"
            />
          </el-form-item>
          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
            <el-button icon="el-icon-refresh" @click="resetSearch">重置</el-button>
          </el-form-item>
        </el-form>
        <div class="action-bar">
          <el-button icon="el-icon-download" size="small" @click="handleExport">导出记录</el-button>
        </div>
      </div>
    </el-card>

    <!-- 表格 -->
    <el-card class="table-card" shadow="never">
      <el-table v-loading="loading" :data="list" border size="small" height="100%">
        <!-- 核销码（当次被扫的原始输入快照，审计用不脱敏） -->
        <el-table-column label="核销码" width="150">
          <template slot-scope="scope">
            <span class="code-text">{{ formatVerifyCode(scope.row.verify_code) }}</span>
          </template>
        </el-table-column>
        <!-- 持券人 -->
        <el-table-column label="持券人" min-width="150">
          <template slot-scope="scope">
            <div class="user-cell">
              <el-image
                v-if="scope.row.avatar"
                class="user-avatar user-avatar--img"
                :src="scope.row.avatar"
                fit="cover"
              >
                <span slot="error" class="user-avatar" :class="{ 'user-avatar--female': scope.row.gender === 2 }">
                  {{ (scope.row.nickname || '客')[0] }}
                </span>
              </el-image>
              <span v-else class="user-avatar" :class="{ 'user-avatar--female': scope.row.gender === 2 }">
                {{ (scope.row.nickname || '客')[0] }}
              </span>
              <div class="user-main">
                <span class="user-name">{{ scope.row.nickname || '未知用户' }}</span>
                <span class="user-id" v-if="scope.row.user_id">编号 {{ scope.row.user_id }}</span>
                <span class="user-id" v-else>非平台券码</span>
              </div>
            </div>
          </template>
        </el-table-column>
        <!-- 券名称 -->
        <el-table-column label="券名称" min-width="170" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.benefit_name || '—' }}</template>
        </el-table-column>
        <!-- 核销结果 -->
        <el-table-column label="核销结果" width="100" align="center">
          <template slot-scope="scope">
            <el-tag :type="verifyResultTagType(scope.row.result)" size="mini" effect="plain">
              {{ verifyResultText(scope.row.result) }}
            </el-tag>
          </template>
        </el-table-column>
        <!-- 失败原因 -->
        <el-table-column label="失败原因" min-width="120" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.result === 1" class="cell-muted">—</span>
            <span v-else class="fail-text">{{ scope.row.fail_reason || '未知原因' }}</span>
          </template>
        </el-table-column>
        <!-- 核销人 -->
        <el-table-column label="核销人" width="100">
          <template slot-scope="scope">{{ scope.row.operator_name || '—' }}</template>
        </el-table-column>
        <!-- 核销时间 -->
        <el-table-column label="核销时间" width="150" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.verify_time }}</template>
        </el-table-column>
      </el-table>
      <div class="pagination-wrap">
        <el-pagination
          :current-page="page"
          :page-size="pageSize"
          :total="total"
          layout="total, prev, pager, next"
          @current-change="handlePageChange"
        />
      </div>
    </el-card>
  </div>
</template>

<script>
import { VERIFY_RESULT, verifyResultText, verifyResultTagType } from '@/mock/benefitVerify'
import { formatVerifyCode } from '@/mock/userBenefit'
import { getVerifyLogList } from '@/api/benefit'

export default {
  name: 'BenefitVerifyManage',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      // 头部统计（后端全量计数，不受当页筛选影响）
      resultCounts: {},
      query: { keyword: '', result: '', dateRange: null },
      page: 1,
      pageSize: 10,
      VERIFY_RESULT,
      verifyResultText,
      verifyResultTagType,
      formatVerifyCode
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    loadData() {
      this.loading = true
      const params = { page: this.page, page_size: this.pageSize }
      const kw = (this.query.keyword || '').trim()
      if (kw) params.keyword = kw
      if (this.query.result !== '' && this.query.result !== null) params.result = this.query.result
      const range = this.query.dateRange || []
      if (range && range.length === 2) params.date_range = range
      getVerifyLogList(params).then(res => {
        if (res.code !== 200) return
        this.list = res.data.list || []
        this.total = res.data.total || 0
        this.resultCounts = res.data.result_counts || {}
      }).finally(() => {
        this.loading = false
      })
    },
    // 头部统计：全部记录按结果计数（后端 result_counts，不受当页筛选影响）
    resultCount(val) {
      return this.resultCounts[String(val)] || 0
    },
    handlePageChange(val) {
      this.page = val
      this.loadData()
    },
    handleSearch() {
      this.page = 1
      this.loadData()
    },
    resetSearch() {
      this.query = { keyword: '', result: '', dateRange: null }
      this.page = 1
      this.loadData()
    },
    handleExport() {
      this.$message.info('导出功能待接入')
    }
  }
}
</script>

<style scoped>
.code-text {
  font-family: Consolas, Monaco, monospace;
  font-size: 12.5px;
  color: #4d4d4d;
  letter-spacing: 0.6px;
}
.user-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}
.user-avatar {
  flex: 0 0 30px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #6b5a45;
  color: #fff;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.user-avatar--female {
  background: #a4686b;
}
/* 真实头像：el-image 内图吃满圆框 */
.user-avatar--img >>> img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
}
.user-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  line-height: 1.35;
}
.user-name {
  font-size: 13px;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.user-id {
  font-size: 11px;
  color: #a8abb2;
}
.cell-muted {
  color: #c0c4cc;
}
.fail-text {
  color: #c45656;
  font-size: 12.5px;
}
.text-danger {
  color: #c45656;
}
</style>
